import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { CourseFamily } from '@/types';
import ClassWizardLayout from './ClassWizardLayout';
import { useClassWizard } from './ClassWizardContext';

const Step3CourseFamily = () => {
  const navigate = useNavigate();
  const { state, setState } = useClassWizard();
  const [courseFamilies, setCourseFamilies] = useState<CourseFamily[]>([]);
  const [filteredFamilies, setFilteredFamilies] = useState<CourseFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!state.selectedSubscription) {
      toast.error('Selecione uma assinatura primeiro');
      navigate('/class-wizard/step2');
      return;
    }
    
    if (!state.selectedSponsors || state.selectedSponsors.length === 0) {
      toast.error('Selecione os solicitantes primeiro');
      navigate('/class-wizard/step3');
      return;
    }
  }, [state.selectedSubscription, state.selectedSponsors, navigate]);

  useEffect(() => {
    fetchCourseFamilies();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [courseFamilies, searchTerm]);

  const fetchCourseFamilies = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('course_families')
        .select('*');

      if (error) throw error;
      
      setCourseFamilies(data || []);
      setFilteredFamilies(data || []);
    } catch (error: any) {
      toast.error('Erro ao carregar famílias de cursos', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!searchTerm) {
      setFilteredFamilies(courseFamilies);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = courseFamilies.filter(family => 
      family.name.toLowerCase().includes(term) ||
      (family.description && family.description.toLowerCase().includes(term))
    );
    
    setFilteredFamilies(filtered);
  };

  const handleSelectFamily = (family: CourseFamily) => {
    setState(prev => ({ ...prev, selectedCourseFamily: family }));
    navigate('/class-wizard/step5');
  };

  return (
    <ClassWizardLayout
      currentStep={4}
      title="Selecionar Família de Cursos"
      description="Escolha uma família de cursos para a nova classe"
      nextDisabled={true}
    >
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar famílias de cursos..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {searchTerm && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSearchTerm('')}
              title="Limpar busca"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            onClick={fetchCourseFamilies}
            disabled={loading}
          >
            Atualizar
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">Carregando famílias de cursos...</div>
        ) : filteredFamilies.length === 0 ? (
          <div className="text-center py-8">Nenhuma família de cursos encontrada</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFamilies.map((family) => (
              <Card 
                key={family.id}
                className={`cursor-pointer hover:border-primary transition-colors ${
                  state.selectedCourseFamily?.id === family.id ? 'border-primary bg-muted/50' : ''
                }`}
                onClick={() => handleSelectFamily(family)}
              >
                <CardHeader className="pb-2">
                  <CardTitle>{family.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="line-clamp-3">
                    {family.description || 'Sem descrição disponível'}
                  </CardDescription>
                </CardContent>
                <CardFooter>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectFamily(family);
                    }}
                  >
                    Selecionar
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ClassWizardLayout>
  );
};

export default Step3CourseFamily; 